import {
  Box,
  Button,
  Container,
  Divider,
  Paper,
  Typography
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { push } from "connected-react-router";
import React, { Fragment, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { ExpandedAssignmentsProvider } from "../../context/ExpandedAssignmentsContext";
import { useCustomer } from "../../hooks/customer";
import { useActivityCompletions } from "../../hooks/learningCenter/activityCompletions";
import { usePolicyPlayer } from "../../hooks/usePolicyPlayer";
import { enqueueSuccessSnackbar } from "../../redux/actions/snackbarActions";
import { learnLearningPath } from "../../routes/LearningCenterRoutes";
import { toFullDate } from "../../utils/datetime";
import BottomNavbar from "../BottomNavbar/BottomNavbar";
import LoadingButton from "../Buttons/LoadingButton";

const contentBuffer = 64; // Buffer for bottom nav bar

const useStyles = makeStyles(theme => ({
  "@global": {
    ".repository-item-link": {
      display: "flex",
      width: "fit-content",
      padding: "0 4px",
      marginLeft: -4
    },
    img: {
      maxWidth: "100%"
    }
  },
  container: {
    display: "flex",
    padding: theme.spacing(0, 8),
    justifyContent: "center"
  },
  policyTitle: {
    fontWeight: 400,
    marginBottom: theme.spacing(0.5)
  },
  customerLogoImg: {
    maxHeight: 68,
    objectFit: "contain",
    objectPosition: "left"
  }
}));

const PolicyPlayerContent = ({ contentRef, content }) => {
  usePolicyPlayer(content);
  const classes = useStyles();

  return (
    <Container maxWidth="md" className={classes.container}>
      <Box mt={5} mb={8} flex={1}>
        <div ref={contentRef} dangerouslySetInnerHTML={{ __html: content }} />
      </Box>
    </Container>
  );
};

const PolicyPlayer = ({
  trainingObjectId,
  courseContentableId,
  content,
  policyVersionTitle,
  manualTitle,
  policyNumber,
  lastUpdatedDate,
  isSelfAssigned,
  navigationButtons,
  isComplete
}) => {
  const classes = useStyles();
  const contentRef = useRef();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { createActivityCompletion, isCreating } = useActivityCompletions();
  const { customerLogoUrl, name: customerName } = useCustomer();

  const [isContentRead, setIsContentRead] = useState(false);
  const [isFinishedReading, setIsFinishedReading] = useState(true);

  const checkIfContentRead = () => {
    const contentEndPosition = contentRef.current.getBoundingClientRect()
      .bottom;
    const isEndVisible =
      contentEndPosition <= window.innerHeight - contentBuffer;
    if (isEndVisible) {
      setIsContentRead(true);
    }
  };

  useEffect(() => {
    setIsContentRead(false);
    if (content) {
      checkIfContentRead();
    }
  }, [content]);

  useEffect(() => {
    window.addEventListener("scroll", checkIfContentRead);

    return () => window.removeEventListener("scroll", checkIfContentRead);
  }, []);

  useEffect(() => {
    setIsFinishedReading(isComplete);
  }, [content, isComplete]);

  const handleFinishReadingClick = () => {
    createActivityCompletion({
      courseContentable: { type: "courseContentable", id: courseContentableId },
      trainingObject: { type: "trainingObject", id: trainingObjectId }
    }).then(response => {
      if (response.ok) {
        setIsFinishedReading(true);
        dispatch(
          enqueueSuccessSnackbar({
            message: t(
              "contentViewer.policyPlayer.finishedReadingSuccessMessage"
            )
          })
        );
      } else {
        dispatch(
          enqueueSuccessSnackbar({
            message: t("contentViewer.policyPlayer.finishedReadingErrorMessage")
          })
        );
      }
    });
  };

  return (
    <Fragment>
      <Container maxWidth="md" disableGutters>
        {lastUpdatedDate && (
          <Box mb={4} ml={0.5}>
            <Typography variant="subtitle2" color="textSecondary">
              {`${t("contentViewer.policyPlayer.lastUpdated")}: ${toFullDate(
                lastUpdatedDate
              )}`}
            </Typography>
          </Box>
        )}
        <Paper>
          <Box
            display="flex"
            p={{ xs: 2, sm: 5 }}
            justifyContent="space-between"
            flexDirection={{ xs: "column-reverse", md: "row" }}
          >
            <Box display="flex" flexDirection="column" mt={{ xs: 4, md: 0 }}>
              <Typography
                variant="h2"
                className={classes.policyTitle}
                color="textSecondary"
              >
                {`${policyVersionTitle || ""} ${
                  !!policyNumber ? `| ${policyNumber}` : ""
                }`}
              </Typography>
              <Typography variant="subtitle2" color="textSecondary">
                {manualTitle}
              </Typography>
            </Box>
            {customerLogoUrl ? (
              <img src={customerLogoUrl} className={classes.customerLogoImg} />
            ) : (
              <Box pt={{ xs: 0, md: 1 }}>
                <Typography variant="h3" color="textSecondary">
                  {customerName}
                </Typography>
              </Box>
            )}
          </Box>
          <Divider />
          <ExpandedAssignmentsProvider>
            <PolicyPlayerContent contentRef={contentRef} content={content} />
          </ExpandedAssignmentsProvider>
        </Paper>
      </Container>
      <PolicyPlayerBottomNavBar
        isCreatingCompletion={isCreating}
        onClickFinishReading={handleFinishReadingClick}
        disableFinishButton={!isContentRead}
        navigationButtons={navigationButtons}
        isFinishedReading={isFinishedReading}
        hideFinishReadingBtn={isSelfAssigned}
        hideBackToAssignmentsBtn={isSelfAssigned}
      />
    </Fragment>
  );
};

const PolicyPlayerBottomNavBar = ({
  disableFinishButton,
  isCreatingCompletion,
  navigationButtons,
  onClickFinishReading,
  isFinishedReading,
  hideBackToAssignmentsBtn,
  hideFinishReadingBtn
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const onClickBackToAssignments = () => dispatch(push(learnLearningPath));

  return (
    <BottomNavbar>
      <Box display="flex" justifyContent="space-between" flex={1}>
        <div>
          {!hideBackToAssignmentsBtn && (
            <Button color="primary" onClick={onClickBackToAssignments}>
              {t("contentViewer.policyPlayer.backToAssignmentsBtn")}
            </Button>
          )}
        </div>
        <Box display="flex">
          <Box display={{ xs: "none", md: "flex" }} pr={3}>
            {navigationButtons}
          </Box>
          {!isFinishedReading && !hideFinishReadingBtn && (
            <LoadingButton
              variant="contained"
              color="primary"
              className="wide"
              pending={isCreatingCompletion}
              disabled={disableFinishButton}
              onClick={onClickFinishReading}
            >
              {t("contentViewer.policyPlayer.finishReadingBtn")}
            </LoadingButton>
          )}
        </Box>
      </Box>
    </BottomNavbar>
  );
};

export default PolicyPlayer;
